import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    model,
    OnDestroy,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { AiTelemetry } from "../../services/ai.telemetry";
import type { TelemetrySession } from "../../services/telemetry/telemetry.session";
import { FormsModule } from "@angular/forms";
import { SimpleMarkdownPipe } from "../../pipes/s25.pipes";
import { ChatMessage, BuildResult, FormWorkflowPlan, plannedFormScopeLines } from "./form.workflow.plan";
import { FormWorkflowBuilderService } from "./form.workflow.builder.service";
import { CacheRepository } from "../../decorators/cache.decorator";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";

@Component({
    selector: "s25-ng-ai-form-workflow-builder",
    standalone: true,
    imports: [FormsModule, SimpleMarkdownPipe, S25IconComponent],
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="ai-builder">
            <div class="ai-builder__chat">
                <div class="ai-builder__messages" #messagesContainer>
                    @for (msg of messages(); track $index) {
                        <div
                            class="ai-builder__message"
                            [class.ai-builder__message--user]="msg.role === 'user'"
                            [class.ai-builder__message--assistant]="msg.role === 'assistant'"
                        >
                            <strong class="ai-builder__role">{{ msg.role === "user" ? "You" : "AI" }}:</strong>
                            <span [innerHTML]="msg.content | markdown"></span>
                        </div>
                    }
                    @if (isPlanning()) {
                        <div class="ai-builder__message ai-builder__message--assistant ai-builder__thinking">
                            <div class="ai-builder__dots"><span></span><span></span><span></span></div>
                            <em>{{ planningStatus() }}</em>
                        </div>
                    }
                </div>
                <div class="ai-builder__input">
                    <textarea
                        [(ngModel)]="userInput"
                        placeholder="Describe the form and workflow you want to create..."
                        rows="3"
                        (keydown.enter)="$event.shiftKey ? null : onSend($event)"
                    ></textarea>
                    @if (inputTooLong()) {
                        <div class="ai-builder__char-warning">
                            Message exceeds the {{ maxMessageLength }} character limit.
                        </div>
                    }
                    <div class="ai-builder__input-buttons">
                        <button class="aw-button aw-button--outline" (click)="onExit()">Exit</button>
                        <button
                            class="aw-button aw-button--primary"
                            (click)="onSend()"
                            [disabled]="!userInput().trim() || isPlanning() || isBuilding() || inputTooLong()"
                        >
                            Send
                        </button>
                    </div>
                </div>
            </div>

            <div class="ai-builder__preview">
                <h3 class="ai-assisted-heading">
                    <s25-ng-icon [type]="'aiSparkles'" [ariaLabel]="false" class="ai-sparkles-adjacent" />
                    Plan Preview
                </h3>
                @if (currentPlan()) {
                    <div class="ai-builder__plan">
                        <h4>Form: {{ currentPlan()!.form.name }}</h4>
                        <p>{{ currentPlan()!.form.description }}</p>
                        @for (line of scopeLines(); track line) {
                            <p>{{ line }}</p>
                        }
                        <h5>Fields ({{ currentPlan()!.form.fields?.length || 0 }}):</h5>
                        <ul>
                            @for (field of currentPlan()!.form.fields || []; track field.fieldLabel) {
                                <li>
                                    {{ field.fieldLabel }} ({{ field.fieldType
                                    }}{{ field.answerType ? " / " + field.answerType : "" }}){{
                                        field.required ? " *" : ""
                                    }}
                                    @if (field.options?.length) {
                                        <span class="c-textMuted"> [{{ field.options.join(", ") }}]</span>
                                    }
                                    @if (field.minLength || field.maxLength) {
                                        <span class="c-textMuted">
                                            len: {{ field.minLength || "?" }}-{{ field.maxLength || "?" }}</span
                                        >
                                    }
                                    @if (field.regexValid) {
                                        <span class="c-textMuted"> regex: {{ field.regexValid }}</span>
                                    }
                                    @if (field.defaultValue) {
                                        <span class="c-textMuted"> default: {{ field.defaultValue }}</span>
                                    }
                                </li>
                            }
                        </ul>

                        @if (currentPlan()!.form.showHideRules?.length) {
                            <h5>Show/Hide Rules ({{ currentPlan()!.form.showHideRules.length }}):</h5>
                            <ul>
                                @for (r of currentPlan()!.form.showHideRules; track r.ruleName) {
                                    <li>
                                        {{ r.ruleName }}: When
                                        @for (c of r.conditions || []; track $index) {
                                            @if ($index) {
                                                AND
                                            }
                                            <strong>{{ c.sourceFieldLabel }}</strong>
                                            {{ c.operator }} [{{ c.values?.join(", ") }}]
                                        }
                                        &rarr; {{ r.action }}
                                        <strong>{{ r.targetFieldLabel }}</strong>
                                    </li>
                                }
                            </ul>
                        }

                        @if (currentPlan()!.form.validationRules?.length) {
                            <h5>Validation Rules ({{ currentPlan()!.form.validationRules.length }}):</h5>
                            <ul>
                                @for (r of currentPlan()!.form.validationRules; track r.ruleName) {
                                    <li>
                                        {{ r.ruleName }}: When
                                        @for (c of r.conditions || []; track $index) {
                                            @if ($index) {
                                                AND
                                            }
                                            <strong>{{ c.sourceFieldLabel }}</strong>
                                            {{ c.operator }} [{{ c.values?.join(", ") }}]
                                        }
                                        &rarr;
                                        <em>{{ r.validationMessage }}</em>
                                    </li>
                                }
                            </ul>
                        }

                        <h4>Workflow: {{ currentPlan()!.workflow.name }}</h4>
                        <h5>Stages ({{ currentPlan()!.workflow.stages?.length || 0 }}):</h5>
                        <ul>
                            @for (stage of currentPlan()!.workflow.stages || []; track stage.stageName) {
                                <li>
                                    {{ stage.stageName }}
                                    @if (stage.isStartStage) {
                                        <span class="tag">Start</span>
                                    }
                                    @if (stage.isTerminal) {
                                        <span class="tag">Terminal</span>
                                    }
                                    @if (stage.isAccumulator) {
                                        <span class="tag tag--accumulator">Accumulator</span>
                                    }
                                    @if (stage.defaultEnabled === false) {
                                        <span class="tag tag--disabled">Disabled by default</span>
                                    }
                                    @if (stage.states?.length) {
                                        <ul class="ai-builder__states">
                                            @for (st of stage.states; track st.stateName) {
                                                <li>
                                                    {{ st.stateName }}
                                                    @if (st.defaultEnabled === false) {
                                                        <span class="tag tag--disabled">Disabled</span>
                                                    }
                                                    @if (st.approvalGroupName) {
                                                        <span class="c-textMuted"> ({{ st.approvalGroupName }})</span>
                                                    }
                                                    @if (st.contactNames?.length) {
                                                        <span class="c-textMuted">
                                                            {{ st.contactNames.join(", ") }}</span
                                                        >
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    }
                                    @if (stage.actions?.length) {
                                        <ul class="ai-builder__actions">
                                            @for (act of stage.actions; track act.description) {
                                                <li>
                                                    <span class="c-textMuted">{{ act.actionCode }}</span>
                                                    on {{ act.transitionType }}
                                                    @if (act.defaultEnabled === false) {
                                                        <span class="tag tag--disabled">Disabled</span>
                                                    }
                                                    @if (act.actionCode === "SPAWN_WORKFLOW") {
                                                        @if (act.spawnReuseSameItem) {
                                                            &mdash; Compose:
                                                            <strong>{{
                                                                act.spawnWorkflowProcessName ||
                                                                    act.spawnWorkflowProcessId
                                                            }}</strong>
                                                        } @else if (act.spawnIteratorFieldLabel) {
                                                            &mdash; Iterator on
                                                            <strong>{{ act.spawnIteratorFieldLabel }}</strong>
                                                            ({{ act.spawnDispatchFieldType }})
                                                            @if (act.dispatchMappings?.length) {
                                                                <ul class="ai-builder__dispatch">
                                                                    @for (
                                                                        dm of act.dispatchMappings;
                                                                        track dm.dispatchValue
                                                                    ) {
                                                                        <li>
                                                                            {{
                                                                                dm.dispatchValueLabel ||
                                                                                    dm.dispatchValue
                                                                            }}
                                                                            &rarr;
                                                                            {{ dm.targetFormName || dm.targetFormId }}
                                                                        </li>
                                                                    }
                                                                </ul>
                                                            }
                                                        } @else if (act.spawnChildFormId) {
                                                            &mdash; Submitter &rarr;
                                                            <strong>{{
                                                                act.spawnChildFormName || act.spawnChildFormId
                                                            }}</strong>
                                                        }
                                                        @if (act.spawnActionHandling) {
                                                            <span class="c-textMuted">
                                                                ({{ act.spawnActionHandling }})</span
                                                            >
                                                        }
                                                    }
                                                </li>
                                            }
                                        </ul>
                                    }
                                </li>
                            }
                        </ul>

                        @if (currentPlan()!.workflow.stageRules?.length) {
                            <h5>Stage Rules ({{ currentPlan()!.workflow.stageRules.length }}):</h5>
                            <ul>
                                @for (r of currentPlan()!.workflow.stageRules; track r.ruleName) {
                                    <li>
                                        {{ r.ruleName }}: When
                                        @for (c of r.conditions || []; track $index) {
                                            @if ($index) {
                                                AND
                                            }
                                            <strong>{{ c.sourceFieldLabel }}</strong>
                                            {{ c.operator }} [{{ c.values?.join(", ") }}]
                                        }
                                        &rarr; {{ r.action }}
                                        <strong>{{ r.targetStageName }}</strong>
                                    </li>
                                }
                            </ul>
                        }

                        @if (currentPlan()!.workflow.stateRules?.length) {
                            <h5>Approval Rules ({{ currentPlan()!.workflow.stateRules.length }}):</h5>
                            <ul>
                                @for (r of currentPlan()!.workflow.stateRules; track r.ruleName) {
                                    <li>
                                        {{ r.ruleName }}: When
                                        @for (c of r.conditions || []; track $index) {
                                            @if ($index) {
                                                AND
                                            }
                                            <strong>{{ c.sourceFieldLabel }}</strong>
                                            {{ c.operator }} [{{ c.values?.join(", ") }}]
                                        }
                                        &rarr; {{ r.action }}
                                        <strong>{{ r.targetStateName }}</strong>
                                        in {{ r.targetStageName }}
                                    </li>
                                }
                            </ul>
                        }

                        @if (currentPlan()!.workflow.actionRules?.length) {
                            <h5>Action Rules ({{ currentPlan()!.workflow.actionRules.length }}):</h5>
                            <ul>
                                @for (r of currentPlan()!.workflow.actionRules; track r.ruleName) {
                                    <li>
                                        {{ r.ruleName }}: When
                                        @for (c of r.conditions || []; track $index) {
                                            @if ($index) {
                                                AND
                                            }
                                            <strong>{{ c.sourceFieldLabel }}</strong>
                                            {{ c.operator }} [{{ c.values?.join(", ") }}]
                                        }
                                        &rarr; {{ r.action }} <strong>{{ r.targetActionCode }}</strong> on
                                        {{ r.targetTransitionType }} in {{ r.targetStageName }}
                                    </li>
                                }
                            </ul>
                        }

                        <div class="ai-builder__build-section">
                            <button
                                class="aw-button aw-button--primary"
                                (click)="onBuild()"
                                [disabled]="isBuilding() || isPlanning()"
                            >
                                {{ isBuilding() ? "Building..." : "BUILD" }}
                            </button>
                        </div>
                    </div>
                } @else {
                    <p class="c-textMuted">No plan yet. Describe what you want and send a message.</p>
                }

                @if (buildResult()) {
                    <div class="ai-builder__result">
                        <h4>Build Result</h4>
                        @if (buildResult()!.ingestionPointId) {
                            <p>Form ID: {{ buildResult()!.ingestionPointId }}</p>
                        }
                        @if (buildResult()!.workflowProcessId) {
                            <p>Workflow ID: {{ buildResult()!.workflowProcessId }}</p>
                        }
                        @if (buildResult()!.warnings?.length) {
                            <div class="ai-builder__warnings">
                                <h5>Warnings:</h5>
                                <ul>
                                    @for (w of buildResult()!.warnings; track w) {
                                        <li>{{ w }}</li>
                                    }
                                </ul>
                            </div>
                        }
                    </div>
                }
            </div>
        </div>
    `,
    styles: `
        .ai-assisted-heading {
            display: flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-sparkles-adjacent {
            flex-shrink: 0;
        }

        .ai-builder {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            min-height: 500px;
        }
        .ai-builder__chat {
            display: flex;
            flex-direction: column;
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }
        .ai-builder__messages {
            flex: 1;
            overflow-y: auto;
            padding: 16px;
            max-height: 400px;
        }
        .ai-builder__message {
            margin-bottom: 12px;
            padding: 8px 12px;
            border-radius: 8px;
        }
        .ai-builder__message--user {
            background: color-mix(in srgb, var(--cn-blue) 15%, var(--color--surface-bg));
            text-align: right;
        }
        .ai-builder__message--assistant {
            background: var(--color--surface-2-bg);
        }
        .ai-builder__role {
            margin-right: 6px;
        }
        .ai-builder__message ul {
            margin: 4px 0 4px 20px;
            padding: 0;
        }
        .ai-builder__message li {
            margin-bottom: 2px;
        }
        .ai-builder__input {
            border-top: 1px solid var(--color--border);
            padding: 12px;
        }
        .ai-builder__input textarea {
            width: 100%;
            resize: vertical;
            margin-bottom: 8px;
        }
        .ai-builder__input-buttons {
            display: flex;
            gap: 8px;
            justify-content: flex-end;
        }
        .ai-builder__preview {
            border: 1px solid var(--color--border);
            border-radius: 8px;
            padding: 16px;
            overflow-y: auto;
            max-height: 550px;
        }
        .ai-builder__build-section {
            margin-top: 16px;
            padding-top: 16px;
            border-top: 1px solid var(--color--border);
        }
        .ai-builder__result {
            margin-top: 16px;
            padding: 12px;
            background: color-mix(in srgb, var(--color--objects-event) 18%, var(--color--surface-bg));
            border-radius: 8px;
        }
        .ai-builder__warnings {
            color: var(--color--text-warning);
        }
        .tag {
            display: inline-block;
            padding: 2px 6px;
            background: var(--color--surface-2-bg-hover);
            border-radius: 4px;
            font-size: 0.8em;
            margin-left: 4px;
        }
        .tag--accumulator {
            background: color-mix(in srgb, var(--color--text-warning) 18%, var(--color--surface-bg));
            color: var(--color--text-warning);
        }
        .tag--disabled {
            background: var(--color--surface-bg-error);
            color: var(--color--danger);
        }
        .ai-builder__states {
            list-style: circle;
            margin: 4px 0 0 16px;
            font-size: 0.9em;
        }
        .ai-builder__actions {
            list-style: disc;
            margin: 4px 0 0 16px;
            font-size: 0.9em;
        }
        .ai-builder__dispatch {
            list-style: circle;
            margin: 2px 0 0 16px;
            font-size: 0.9em;
        }
        .ai-builder__char-warning {
            color: var(--color--danger);
            font-size: 0.85em;
            margin-bottom: 4px;
        }
        .ai-builder__thinking {
            display: flex;
            align-items: center;
            gap: 10px;
        }
        .ai-builder__dots {
            display: flex;
            gap: 4px;
        }
        .ai-builder__dots span {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: var(--color--text-muted);
            animation: ai-dot-bounce 1.4s ease-in-out infinite;
        }
        .ai-builder__dots span:nth-child(2) {
            animation-delay: 0.2s;
        }
        .ai-builder__dots span:nth-child(3) {
            animation-delay: 0.4s;
        }
        @keyframes ai-dot-bounce {
            0%,
            80%,
            100% {
                transform: scale(0.6);
                opacity: 0.4;
            }
            40% {
                transform: scale(1);
                opacity: 1;
            }
        }
    `,
})
export class AiFormWorkflowBuilderComponent implements OnDestroy {
    static readonly MAX_MESSAGE_LENGTH = 4_000;

    private static readonly PLANNING_MESSAGES = [
        "Analyzing your request...",
        "Gathering requirements...",
        "Clarifying details...",
        "Looking up data...",
        "Assessing results...",
        "Setting context...",
        "Designing the form...",
        "Adding any fields...",
        "Building the workflow...",
        "Adding any actions...",
        "Configuring rules...",
        "Putting everything together...",
        "Validating the plan...",
        "Almost there...",
        "Finalizing the plan...",
    ];

    exit = output<void>();
    messages = signal<ChatMessage[]>([]);
    currentPlan = signal<FormWorkflowPlan | null>(null);
    buildResult = signal<BuildResult | null>(null);
    isPlanning = signal(false);
    isBuilding = signal(false);
    planningStatus = signal("Analyzing your request...");
    maxMessageLength = AiFormWorkflowBuilderComponent.MAX_MESSAGE_LENGTH;
    userInput = model("");
    readonly inputTooLong = computed(() => this.userInput().length > AiFormWorkflowBuilderComponent.MAX_MESSAGE_LENGTH);
    readonly scopeLines = computed(() => plannedFormScopeLines(this.currentPlan()?.form));

    private messagesContainer = viewChild<ElementRef<HTMLElement>>("messagesContainer");
    private planningTimer: ReturnType<typeof setInterval> | null = null;
    private readonly aiSession: TelemetrySession = AiTelemetry.createSession("FormWorkflowBuilder");

    constructor() {
        this.aiSession.open();
        effect(() => {
            this.messages();
            this.isPlanning();
            setTimeout(() => {
                const el = this.messagesContainer()?.nativeElement;
                if (el) el.scrollTop = el.scrollHeight;
            });
        });
    }

    ngOnDestroy(): void {
        this.aiSession.close();
    }

    onExit(): void {
        this.exit.emit();
    }

    async onSend(event?: Event) {
        event?.preventDefault();
        const text = this.userInput().trim();
        if (!text || this.isPlanning() || this.isBuilding()) return;

        this.aiSession.engage();
        this.userInput.set("");
        this.messages.update((msgs) => [...msgs, { role: "user", content: text }]);
        this.isPlanning.set(true);
        this.startPlanningTimer();

        try {
            const allMessages = this.messages().map((m) => ({ role: m.role, content: m.content }));
            const response = await FormWorkflowBuilderService.plan(allMessages);
            if (response.plan) {
                this.currentPlan.set(response.plan);
            }
            const fallback = response.plan
                ? "This plan is not saved yet. Review it and click Build to create the form."
                : "I wasn't able to generate a plan. Try rephrasing your request.";
            const chatResponse = response.chatResponse ?? "";
            const hasServerFailure =
                chatResponse.includes("Remaining issues") || chatResponse.includes("No changes applied");
            const content =
                response.plan || hasServerFailure
                    ? AiFormWorkflowBuilderComponent.cleanChatResponse(chatResponse, fallback)
                    : fallback;
            this.messages.update((msgs) => [...msgs, { role: "assistant", content }]);
        } catch (e: any) {
            this.messages.update((msgs) => [
                ...msgs,
                { role: "assistant", content: "Error: " + AiFormWorkflowBuilderComponent.extractErrorMessage(e) },
            ]);
        } finally {
            this.stopPlanningTimer();
            this.isPlanning.set(false);
        }
    }

    async onBuild() {
        const plan = this.currentPlan();
        if (!plan || this.isBuilding()) return;

        this.isBuilding.set(true);
        this.buildResult.set(null);

        try {
            const result = await FormWorkflowBuilderService.build(plan);
            CacheRepository.invalidateByService("RuleTreeService");
            this.buildResult.set(result);
            if (result.ingestionPointId || result.workflowProcessId) {
                this.aiSession.complete();
            }
        } catch (e: any) {
            this.buildResult.set({
                ingestionPointId: null,
                workflowProcessId: null,
                warnings: ["Build failed: " + AiFormWorkflowBuilderComponent.extractErrorMessage(e)],
            });
        } finally {
            this.isBuilding.set(false);
        }
    }

    private startPlanningTimer() {
        let step = 0;
        this.planningStatus.set(AiFormWorkflowBuilderComponent.PLANNING_MESSAGES[0]);
        this.planningTimer = setInterval(() => {
            step = Math.min(step + 1, AiFormWorkflowBuilderComponent.PLANNING_MESSAGES.length - 1);
            this.planningStatus.set(AiFormWorkflowBuilderComponent.PLANNING_MESSAGES[step]);
        }, 8_000);
    }

    private stopPlanningTimer() {
        if (this.planningTimer) {
            clearInterval(this.planningTimer);
            this.planningTimer = null;
        }
    }

    private static cleanChatResponse(raw: string | null | undefined, fallback: string): string {
        if (!raw) return fallback;
        const text = raw
            .replace(/`{1,}[\s]*json?\s*/gi, "") // opening fences: ```json, ``json, `json, etc.
            .replace(/`{1,}\s*/g, "") // closing fences or stray backticks
            .replace(/\{[\s\S]*"form"\s*:[\s\S]*\}/g, "") // raw JSON plan objects
            .replace(/\{[\s\S]*"workflow"\s*:[\s\S]*\}/g, "")
            .trim();
        return text || fallback;
    }

    private static extractErrorMessage(e: any): string {
        const body = e?.error || e?.data;
        if (body?.warnings?.length) return body.warnings.join("; ");
        if (body?.chatResponse) return body.chatResponse;
        if (e?.status === 403) return "Workflow and Form Assistance is not enabled for this instance.";
        if (e?.status >= 500) return "A server error occurred. Please try again.";
        if (e?.status > 0) return "Request failed (status " + e.status + "). Please try again.";
        if (typeof body === "string") return body;
        return e?.statusText || "Something went wrong. Please try again.";
    }
}
